import { FC, useRef } from "react";
import { SearchTag } from "@/components/data-table/search-tag";
import SearchSyntaxHelper from "@/components/data-table/search-syntax-helper";
import { parseSearchTags } from "@/pages/legislation/utils/tableUtils";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  DynamicListWidgetConfig,
  SOURCES_COLUMNS,
  WORKSPACE_DOCUMENT_COLUMNS,
} from "@/models/dashboard/WidgetConfig";
import FilterBuilder from "./FilterBuilder";
import {
  isWidgetColumnVisible,
  resolveVisibleWidgetColumns,
  toggleWidgetColumn,
} from "../lib/widgetColumns";
import { Trash2 } from "lucide-react";

interface DynamicListConfigFormProps {
  config: DynamicListWidgetConfig;
  onChange: (config: DynamicListWidgetConfig) => void;
}

const SOURCES_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "published_on", label: "Udgivelsesdato" },
  { value: "Title", label: "Titel" },
];

const WORKSPACE_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: "updatedAt", label: "Opdateret" },
  { value: "createdAt", label: "Oprettet" },
  { value: "name", label: "Navn" },
  { value: "workspace", label: "Workspace" },
  { value: "type", label: "Type" },
];

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50];

const DynamicListConfigForm: FC<DynamicListConfigFormProps> = ({
  config,
  onChange,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const columns =
    config.dataSource === "sources"
      ? SOURCES_COLUMNS
      : WORKSPACE_DOCUMENT_COLUMNS;

  const updateConfig = (updates: Partial<DynamicListWidgetConfig>) => {
    onChange({ ...config, ...updates });
  };

  const handleAddSearchTag = (value: string) => {
    const newTags = parseSearchTags(value);
    if (newTags.length === 0) return;
    const existing = config.searchTags ?? [];
    updateConfig({ searchTags: [...existing, ...newTags], searchQuery: "" });
    if (searchInputRef.current) searchInputRef.current.value = "";
  };

  const handleRemoveSearchTag = (index: number) => {
    const next = (config.searchTags ?? []).filter((_, i) => i !== index);
    updateConfig({ searchTags: next.length > 0 ? next : undefined });
  };

  const handleClearSearchTags = () => {
    updateConfig({ searchTags: undefined, searchQuery: "" });
  };

  const handleColumnToggle = (columnId: string, checked: boolean) => {
    updateConfig({
      visibleColumns: toggleWidgetColumn(
        columns,
        config.visibleColumns,
        columnId,
        checked
      ),
    });
  };

  const visibleColumns = resolveVisibleWidgetColumns(
    columns,
    config.visibleColumns
  );

  const sortOptions =
    config.dataSource === "workspace-documents"
      ? WORKSPACE_SORT_OPTIONS
      : SOURCES_SORT_OPTIONS;

  return (
    <div className="space-y-6">
      {/* Data Source */}
      <div className="space-y-2">
        <Label>Datakilde</Label>
        <Select
          value={config.dataSource}
          onValueChange={(value: "sources" | "workspace-documents") =>
            updateConfig({
              dataSource: value,
              visibleColumns: [],
              filters: {},
              sortBy:
                value === "workspace-documents" ? "updatedAt" : "published_on",
              searchQuery: "",
              searchTags: undefined,
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sources">Kilder (Lovgivning)</SelectItem>
            <SelectItem value="workspace-documents">
              Workspace dokumenter
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Search Query + Tags (legislation only) */}
      {config.dataSource === "sources" && (
        <div className="space-y-2">
          <Label>Søgeord</Label>
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="relative flex items-center">
              <Input
                ref={searchInputRef}
                placeholder="Søg… tryk Enter for at tilføje tag"
                defaultValue={config.searchQuery || ""}
                onChange={(e) => updateConfig({ searchQuery: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const val = (e.target as HTMLInputElement).value.trim();
                    if (val) handleAddSearchTag(val);
                  }
                }}
                className="h-8 w-[260px] pr-8 text-xs"
              />
              <SearchSyntaxHelper />
            </div>
            {(config.searchTags ?? []).map((tag, i) => (
              <SearchTag
                key={`${tag}-${i}`}
                tag={tag}
                onRemove={() => handleRemoveSearchTag(i)}
              />
            ))}
            {(config.searchTags ?? []).length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleClearSearchTags}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ryd søgetags</TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Tryk Enter for at låse en søgeterm som tag. Understøtter avanceret
            søgesyntaks.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="space-y-2">
        <FilterBuilder
          dataSource={config.dataSource}
          filters={config.filters}
          onChange={(filters) => updateConfig({ filters })}
        />
      </div>

      {/* Display — the widget on the grid shows the result as you change these */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Visning</h3>

        <div className="space-y-2">
          <Label>Kolonner</Label>
          <div className="grid grid-cols-2 gap-2">
            {columns.map((column) => {
              const isChecked = isWidgetColumnVisible(
                column,
                config.visibleColumns
              );

              return (
                <div key={column.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`dynamic-col-${column.id}`}
                    checked={isChecked}
                    // An empty selection means "the defaults"; keep one column ticked.
                    disabled={isChecked && visibleColumns.length === 1}
                    onCheckedChange={(checked) =>
                      handleColumnToggle(column.id, checked === true)
                    }
                  />
                  <Label
                    htmlFor={`dynamic-col-${column.id}`}
                    className="font-normal"
                  >
                    {column.label}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="dynamic-sort-by">Sortér efter</Label>
            <Select
              value={config.sortBy}
              onValueChange={(value) => updateConfig({ sortBy: value })}
            >
              <SelectTrigger id="dynamic-sort-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dynamic-sort-direction">Retning</Label>
            <Select
              value={config.sortAscending ? "asc" : "desc"}
              onValueChange={(value) =>
                updateConfig({ sortAscending: value === "asc" })
              }
            >
              <SelectTrigger id="dynamic-sort-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Nyeste først</SelectItem>
                <SelectItem value="asc">Ældste først</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dynamic-page-size">Pr. side</Label>
            <Select
              value={config.pageSize.toString()}
              onValueChange={(value) =>
                updateConfig({ pageSize: parseInt(value, 10) })
              }
            >
              <SelectTrigger id="dynamic-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicListConfigForm;
